import React from 'react';

// Renders the legal pages (Privacy, Terms, Safety) from src/content/legal.json.
// To change the wording, edit that file (and re-run `node scripts/build-legal-html.mjs`
// so public/privacy.html and public/terms.html stay in sync).

export type LegalBlock =
  | { t: 'p'; text: string }
  | { t: 'h'; text: string }
  | { t: 'ul'; items: string[] }
  | { t: 'ol'; style: 'num' | 'alpha'; items: string[] }
  | { t: 'lines'; items: string[] };

export interface LegalDocData {
  title: string;
  updated: string | null;
  intro: string[];
  sections: { title: string; blocks: LegalBlock[] }[];
}

// Turns email addresses into mailto links.
function linkify(text: string): React.ReactNode {
  const parts = text.split(/([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g);
  return parts.map((part, i) =>
    /@/.test(part) && i % 2 === 1
      ? <a key={i} href={`mailto:${part}`} className="text-brand-accent hover:underline">{part}</a>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

const bodyClass = 'text-sm text-zinc-500 leading-relaxed';

function renderBlock(block: LegalBlock): React.ReactNode {
  switch (block.t) {
    case 'p':
      return <p className={bodyClass}>{linkify(block.text)}</p>;
    case 'h':
      return <h3 className="text-sm font-semibold pt-1" style={{ color: 'var(--color-brand-text)' }}>{block.text}</h3>;
    case 'lines':
      return (
        <p className={bodyClass}>
          {block.items.map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{linkify(l)}</React.Fragment>)}
        </p>
      );
    case 'ul':
      return (
        <ul className={`${bodyClass} list-disc pl-5 space-y-1`}>
          {block.items.map((it, i) => <li key={i}>{linkify(it)}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol className={`${bodyClass} pl-7 space-y-1 ${block.style === 'alpha' ? 'list-[lower-alpha]' : 'list-decimal'}`}>
          {block.items.map((it, i) => <li key={i}>{linkify(it)}</li>)}
        </ol>
      );
  }
}

export default function LegalDoc({ doc, icon, tagline }: { doc: LegalDocData; icon: React.ReactNode; tagline?: string }) {
  return (
    <div className="max-w-2xl mx-auto py-10 px-4 space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div
          className="w-12 h-12 border border-brand-border rounded-xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'var(--color-brand-card)' }}
        >
          {icon}
        </div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--color-brand-text)' }}>
          {doc.title}
        </h1>
        <p className="text-zinc-500 text-sm">
          {tagline ?? `Last updated: ${doc.updated}`}
        </p>
      </div>

      {/* Intro */}
      {doc.intro.length > 0 && (
        <div
          className="p-5 rounded-2xl border border-brand-border space-y-3"
          style={{ background: 'var(--color-brand-card)' }}
        >
          {doc.intro.map((p, i) => <p key={i} className={bodyClass}>{linkify(p)}</p>)}
        </div>
      )}

      {/* Sections */}
      <div className="space-y-7">
        {doc.sections.map(section => (
          <div key={section.title} className="space-y-2.5">
            <h2 className="text-base font-semibold" style={{ color: 'var(--color-brand-text)' }}>
              {section.title}
            </h2>
            {section.blocks.map((b, i) => <React.Fragment key={i}>{renderBlock(b)}</React.Fragment>)}
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-zinc-600 pt-4">
        {doc.updated ? `Last updated: ${doc.updated}` : ''}{doc.updated ? ' · ' : ''}
        <a href="mailto:new@yhuu.life" className="text-brand-accent hover:underline">new@yhuu.life</a>
      </p>
    </div>
  );
}
